'use client';

import { useId, type ReactNode } from 'react';

import {
  accountPriorityOutcomes,
  accountSignalCards,
  accountTiers,
  priorityAccountBrief as brief,
  weeklyAccountPriorities
} from './account-prioritization-content';
import styles from './account-prioritization-diagram.module.css';
import { accountDiagram, getDiagramStageState } from './use-case-diagram-data';

type IconKind =
  | 'people'
  | 'building'
  | 'document'
  | 'news'
  | 'database'
  | 'bars'
  | 'bulb'
  | 'target'
  | 'person';

/** Filled illustration icons, with the same silhouettes as the reference. */
function DiagramIcon({
  kind,
  x,
  y,
  size = 32,
  fill = 'currentColor'
}: {
  kind: IconKind;
  x?: number;
  y?: number;
  size?: number;
  fill?: string;
}) {
  const paths: Partial<Record<IconKind, string>> = {
    people:
      'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7-1a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm14 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM5 12c-2.8 0-5 1.6-5 3.5V19h4v-3c0-1.4.6-2.6 1.6-3.9L5 12Zm14 0-.6.1c1 1.3 1.6 2.5 1.6 3.9v3h4v-3.5c0-1.9-2.2-3.5-5-3.5Zm-7 1c-3.9 0-7 1.9-7 4.2V22h14v-4.8c0-2.3-3.1-4.2-7-4.2Z',
    building:
      'M3 22V5l11-4v21H3Zm3-3h2v-3H6v3Zm4 0h2v-3h-2v3Zm-4-5h2v-3H6v3Zm4 0h2v-3h-2v3ZM6 9h2V6H6v3Zm4 0h2V6h-2v3Zm6 13V9h5v13h-5Zm2-3h1v-2h-1v2Zm0-4h1v-2h-1v2Z',
    document:
      'M5 1h10l6 6v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Zm9 2v6h6L14 3ZM8 12v2h9v-2H8Zm0 4v2h9v-2H8Z',
    news: 'M3 3h18a1 1 0 0 1 1 1v17H3a2 2 0 0 1-2-2V6h2v13h1V4a1 1 0 0 1 1-1ZM7 7v6h5V7H7Zm7 0v2h5V7h-5Zm0 4v2h5v-2h-5Zm-7 4v2h12v-2H7Z',
    bars: 'M3 14h3a1 1 0 0 1 1 1v7H2v-7a1 1 0 0 1 1-1Zm8-6h3a1 1 0 0 1 1 1v13h-5V9a1 1 0 0 1 1-1Zm8-7h3a1 1 0 0 1 1 1v20h-5V2a1 1 0 0 1 1-1Z',
    person:
      'M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-5 0-9 2.4-9 5.4V23h18v-3.6c0-3-4-5.4-9-5.4Z'
  };
  return (
    <svg
      x={x}
      y={y}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      aria-hidden="true"
    >
      {paths[kind] && (
        <path
          d={paths[kind]}
          fillRule="evenodd"
        />
      )}
      {kind === 'database' && (
        <>
          <ellipse
            cx="12"
            cy="5"
            rx="9"
            ry="4"
          />
          <path d="M3 7.5C6 11 18 11 21 7.5V12c0 5.3-18 5.3-18 0V7.5Zm0 8C6 19 18 19 21 15.5V20c0 5.3-18 5.3-18 0v-4.5Z" />
        </>
      )}
      {kind === 'bulb' && (
        <>
          <path d="M8 19h8v2H8zm2 3h4v2h-4zm-2-5v-2a7 7 0 1 1 8 0v2H8Z" />
          <path
            d="M12 0V-2M3 2 1 0M0 8h-2M21 2l2-2M24 8h2"
            fill="none"
            stroke={fill}
            strokeWidth="1.4"
          />
        </>
      )}
      {kind === 'target' && (
        <g
          fill="none"
          stroke={fill}
          strokeWidth="2"
        >
          <path d="M20.4 8.1A9 9 0 1 1 15.9 3.6M16 10a4.5 4.5 0 1 1-2-2" />
          <path
            d="m12 12 9-9m-5 0h5v5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}
    </svg>
  );
}

function Lines({
  x,
  y,
  lines,
  size = 22,
  leading = 28,
  weight = 400,
  fill = 'var(--diagram-body)',
  anchor = 'start'
}: {
  x: number;
  y: number;
  lines: readonly string[];
  size?: number;
  leading?: number;
  weight?: number;
  fill?: string;
  anchor?: 'start' | 'middle';
}) {
  return (
    <text
      x={x}
      y={y}
      fontSize={size}
      fontWeight={weight}
      fill={fill}
      textAnchor={anchor}
    >
      {lines.map((line, i) => (
        <tspan
          key={`${i}-${line}`}
          x={x}
          dy={i === 0 ? 0 : leading}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
}

function Stage({
  index,
  focus,
  prefix,
  heading,
  children
}: {
  index: number;
  focus: number;
  prefix: string;
  heading: ReactNode;
  children: ReactNode;
}) {
  const stage = accountDiagram.stages[index];
  const active = focus === index;
  const dimmed = focus >= 0 && !active;
  const [cx, cy, r] = stage.badge;
  return (
    <g
      className={styles.stage}
      data-stage={index}
      data-dimmed={dimmed}
      data-complete={focus === 4}
      data-disclosure-state={getDiagramStageState(
        focus,
        index,
        accountDiagram.stages.length
      )}
    >
      <path
        d={stage.path}
        fill={`url(#${prefix}-panel)`}
        stroke={active ? 'var(--diagram-active)' : 'var(--diagram-rule)'}
        strokeWidth={active ? 2.6 : 1.15}
        className={styles.panel}
      />
      {heading}
      <g className={styles.stageDetail}>{children}</g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={`url(#${prefix}-badge)`}
      />
      <text
        x={cx}
        y={cy + 10}
        textAnchor="middle"
        fontSize="32"
        fontWeight="700"
        fill="#082e2e"
      >
        {index + 1}
      </text>
    </g>
  );
}

function NativeAccountScene({
  focus,
  prefix
}: {
  focus: number;
  prefix: string;
}) {
  const green = `url(#${prefix}-green)`;
  const heading = 'var(--diagram-ink)';
  return (
    <>
      <defs>
        <linearGradient
          id={`${prefix}-panel`}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop stopColor="var(--diagram-panel-start)" />
          <stop
            offset="0.5"
            stopColor="var(--diagram-panel-mid)"
          />
          <stop
            offset="1"
            stopColor="var(--diagram-panel-end)"
          />
        </linearGradient>
        <linearGradient
          id={`${prefix}-green`}
          x1="0"
          y1="0"
          x2="0.8"
          y2="1"
        >
          <stop stopColor="var(--diagram-green-start)" />
          <stop
            offset="0.5"
            stopColor="var(--diagram-green-mid)"
          />
          <stop
            offset="1"
            stopColor="var(--diagram-green-end)"
          />
        </linearGradient>
        <linearGradient
          id={`${prefix}-strong`}
          x1="0"
          y1="0"
          x2="0.8"
          y2="1"
        >
          <stop stopColor="#18835b" />
          <stop
            offset="1"
            stopColor="#075737"
          />
        </linearGradient>
        <linearGradient
          id={`${prefix}-badge`}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop stopColor="#8cdebc" />
          <stop
            offset="1"
            stopColor="#c2ebdc"
          />
        </linearGradient>
        <radialGradient
          id={`${prefix}-disc`}
          cx="0.25"
          cy="0.2"
          r="0.9"
        >
          <stop stopColor="var(--diagram-disc-start)" />
          <stop
            offset="1"
            stopColor="var(--diagram-disc-end)"
          />
        </radialGradient>
        <linearGradient
          id={`${prefix}-logo`}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop stopColor="#092e45" />
          <stop
            offset="1"
            stopColor="#041c2a"
          />
        </linearGradient>
        <linearGradient
          id={`${prefix}-tier-light`}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop stopColor="var(--diagram-tier-light-start)" />
          <stop
            offset="1"
            stopColor="var(--diagram-tier-light-end)"
          />
        </linearGradient>
        <linearGradient
          id={`${prefix}-tier-mid`}
          x1="0"
          y1="0"
          x2="0.8"
          y2="1"
        >
          <stop stopColor="var(--diagram-tier-mid-start)" />
          <stop
            offset="1"
            stopColor="var(--diagram-tier-mid-end)"
          />
        </linearGradient>
      </defs>

      <Stage
        index={0}
        focus={focus}
        prefix={prefix}
        heading={
          <Lines
            x={94}
            y={162}
            lines={['Your account tiers']}
            size={30}
            weight={700}
            fill={heading}
          />
        }
      >
        <path
          d="M126 225H315Q323 225 326 236L351 315Q354 324 344 324H105Q95 324 98 315L117 236Q119 225 126 225Z"
          fill={`url(#${prefix}-tier-light)`}
        />
        <DiagramIcon
          kind="people"
          x={117}
          y={265}
          size={38}
          fill={green}
        />
        <Lines
          x={170}
          y={253}
          lines={['Tier 3']}
          size={26}
          weight={700}
          fill={heading}
        />
        <Lines
          x={170}
          y={282}
          lines={accountTiers[0].lines}
          size={21}
          leading={25}
        />
        <path
          d="M111 336H334Q344 336 347 347L375 431Q378 445 365 445H89Q76 445 79 432L102 347Q104 336 111 336Z"
          fill={`url(#${prefix}-tier-mid)`}
        />
        <DiagramIcon
          kind="building"
          x={119}
          y={375}
          size={46}
          fill={green}
        />
        <Lines
          x={196}
          y={371}
          lines={['Tier 2']}
          size={27}
          weight={700}
          fill={heading}
        />
        <Lines
          x={196}
          y={401}
          lines={accountTiers[1].lines}
          size={22}
          leading={26}
        />
        <path
          d="M82 457H369Q381 457 385 471L418 573Q422 589 406 589H58Q43 589 47 574L71 471Q74 457 82 457Z"
          fill={`url(#${prefix}-strong)`}
        />
        <path
          d="M84 458H367"
          stroke="#7bc8a8"
          strokeOpacity="0.6"
          strokeWidth="2"
        />
        <DiagramIcon
          kind="building"
          x={90}
          y={503}
          size={52}
          fill="#effcf7"
        />
        <Lines
          x={176}
          y={499}
          lines={['Tier 1']}
          size={28}
          weight={700}
          fill="#fff"
        />
        <Lines
          x={176}
          y={529}
          lines={accountTiers[2].lines}
          size={24}
          leading={29}
          weight={500}
          fill="#effcf7"
        />
      </Stage>

      <Stage
        index={1}
        focus={focus}
        prefix={prefix}
        heading={
          <Lines
            x={660}
            y={177}
            lines={['Lumnis stacks signals']}
            size={29}
            weight={700}
            fill={heading}
            anchor="middle"
          />
        }
      >
        {accountSignalCards.map((card, i) => {
          const x = 486 + (i % 2) * 174;
          const y = i < 2 ? 222 : 414;
          const cx = x + 79;
          return (
            <g key={card.icon}>
              <rect
                x={x}
                y={y}
                width="160"
                height="185"
                rx="17"
                fill="var(--diagram-surface)"
                fillOpacity="0.85"
                stroke="var(--diagram-rule)"
              />
              <circle
                cx={cx}
                cy={y + 47}
                r="31"
                fill={`url(#${prefix}-disc)`}
              />
              <DiagramIcon
                kind={card.icon}
                x={cx - 17}
                y={y + 29}
                size={34}
                fill={green}
              />
              <Lines
                x={cx}
                y={y + 101}
                lines={card.title}
                size={i === 2 ? 18.5 : 20}
                leading={24}
                weight={700}
                fill={heading}
                anchor="middle"
              />
            </g>
          );
        })}
      </Stage>

      <Stage
        index={2}
        focus={focus}
        prefix={prefix}
        heading={
          <Lines
            x={1020}
            y={171}
            lines={['This week’s', 'priorities']}
            size={30}
            leading={33}
            weight={700}
            fill={heading}
            anchor="middle"
          />
        }
      >
        {weeklyAccountPriorities.map((priority, i) => {
          const x = [897, 917, 941, 971][i];
          const y = [223, 315, 407, 512][i];
          const w = [248, 207, 160, 99][i];
          const h = [82, 82, 95, 53][i];
          return (
            <g key={priority.count}>
              <path
                d={`M${x + 8} ${y}H${x + w - 8}Q${x + w} ${y} ${x + w - 2} ${y + 10}L${x + w - 17} ${y + h - 8}Q${x + w - 19} ${y + h} ${x + w - 27} ${y + h}H${x + 27}Q${x + 19} ${y + h} ${x + 17} ${y + h - 8}L${x + 2} ${y + 10}Q${x} ${y} ${x + 8} ${y}Z`}
                fill={
                  i === 3
                    ? `url(#${prefix}-strong)`
                    : `url(#${prefix}-tier-mid)`
                }
              />
              <Lines
                x={1021}
                y={y + (i === 3 ? 39 : 37)}
                lines={[priority.count]}
                size={i === 3 ? 40 : 35}
                weight={700}
                fill={i === 3 ? '#fff' : heading}
                anchor="middle"
              />
              {i < 3 && (
                <Lines
                  x={1021}
                  y={y + 65}
                  lines={priority.lines}
                  size={19.5}
                  leading={21}
                  anchor="middle"
                />
              )}
            </g>
          );
        })}
        <Lines
          x={1021}
          y={586}
          lines={['prioritized for', 'outreach']}
          size={22}
          leading={26}
          anchor="middle"
        />
      </Stage>

      <Stage
        index={3}
        focus={focus}
        prefix={prefix}
        heading={
          <Lines
            x={1333}
            y={153}
            lines={['Your account brief']}
            size={32}
            weight={700}
            fill={heading}
          />
        }
      >
        <rect
          x="1230"
          y="179"
          width="494"
          height="450"
          rx="24"
          fill="var(--diagram-surface)"
          stroke="var(--diagram-rule)"
          strokeWidth="1.2"
        />
        <rect
          x="1253"
          y="203"
          width="72"
          height="76"
          rx="15"
          fill={`url(#${prefix}-logo)`}
        />
        <path
          d="M1268 204h43"
          stroke="#3e6378"
          strokeWidth="1.5"
        />
        <text
          x="1289"
          y="258"
          textAnchor="middle"
          fill="#fff"
          fontWeight="600"
          fontSize="49"
        >
          B
        </text>
        <Lines
          x={1348}
          y={233}
          lines={[brief.company]}
          size={31}
          weight={700}
          fill={heading}
        />
        <rect
          x="1348"
          y="247"
          width="94"
          height="32"
          rx="16"
          fill="#b9ecd6"
        />
        <circle
          cx="1365"
          cy="263"
          r="5"
          fill="#085d43"
        />
        <Lines
          x={1378}
          y={270}
          lines={[brief.tier]}
          size={19}
          weight={600}
          fill="#133e34"
        />
        <rect
          x="1452"
          y="247"
          width="224"
          height="32"
          rx="16"
          fill="var(--diagram-chip)"
        />
        <Lines
          x={1467}
          y={270}
          lines={[brief.freshness]}
          size={19}
        />

        <DiagramIcon
          kind="bulb"
          x={1251}
          y={312}
          size={23}
          fill={green}
        />
        <Lines
          x={1285}
          y={331}
          lines={['Why now']}
          size={20}
          weight={700}
          fill={heading}
        />
        <Lines
          x={1390}
          y={326}
          lines={[
            'Buyer engagement, a new security',
            'leader, and hiring point to a',
            'timely conversation.'
          ]}
          size={19}
          leading={26}
        />
        <path
          d="M1253 396H1702"
          stroke="var(--diagram-divider)"
        />

        <DiagramIcon
          kind="news"
          x={1251}
          y={402}
          size={23}
          fill={green}
        />
        <Lines
          x={1285}
          y={421}
          lines={['Evidence']}
          size={20}
          weight={700}
          fill={heading}
        />
        {brief.evidence.map((item, i) => (
          <g key={item.date}>
            <rect
              x="1253"
              y={434 + i * 27}
              width="64"
              height="25"
              rx="6"
              fill="var(--diagram-chip)"
            />
            <Lines
              x={1285}
              y={452 + i * 27}
              lines={[item.date]}
              size={18}
              weight={500}
              anchor="middle"
            />
            <Lines
              x={1330}
              y={452 + i * 27}
              lines={[item.text]}
              size={19.5}
            />
          </g>
        ))}
        <path
          d="M1253 518H1702"
          stroke="var(--diagram-divider)"
        />

        <DiagramIcon
          kind="target"
          x={1251}
          y={525}
          size={24}
          fill={green}
        />
        <Lines
          x={1285}
          y={545}
          lines={['Next move']}
          size={20}
          weight={700}
          fill={heading}
        />
        <Lines
          x={1408}
          y={543}
          lines={['Send a tailored note about', 'platform security hiring.']}
          size={19}
          leading={26}
        />
        <path
          d="M1253 584H1702"
          stroke="var(--diagram-divider)"
        />
        <DiagramIcon
          kind="person"
          x={1251}
          y={597}
          size={23}
          fill={green}
        />
        <Lines
          x={1285}
          y={616}
          lines={['Owner']}
          size={20}
          weight={700}
          fill={heading}
        />
        <Lines
          x={1390}
          y={615}
          lines={[brief.owner]}
          size={19}
        />
      </Stage>

      {[433, 866, 1177].map((x, index) => (
        <g
          key={x}
          className={styles.connector}
          data-active={focus === index || focus === -1}
          data-revealed={focus < 0 || focus > index}
        >
          <path
            d={`M${x - 20} 379H${x + 14}m-11-11 12 11-12 11`}
            stroke={green}
            strokeWidth="7"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ))}

      <g
        className={styles.outcomeGroup}
        data-dimmed={focus >= 0 && focus < 4}
        data-active={focus === 4}
        data-revealed={focus < 0 || focus >= 4}
      >
        <path
          d="M48 746H797M974 746H1725"
          stroke="var(--diagram-rule)"
          strokeWidth="1.2"
        />
        <text
          x="887"
          y="752"
          textAnchor="middle"
          fill="var(--diagram-muted)"
          fontSize="17"
          fontWeight="600"
          letterSpacing="5"
        >
          OUTCOMES
        </text>
        {accountPriorityOutcomes.map((item, i) => {
          const region = accountDiagram.outcomeRegions[i];
          return (
            <g key={item.text}>
              <rect
                {...region}
                rx={region.height / 2}
                fill={`url(#${prefix}-panel)`}
                stroke={
                  focus === 4 ? 'var(--diagram-active)' : 'var(--diagram-rule)'
                }
                strokeWidth={focus === 4 ? 2.6 : 1.1}
              />
              <DiagramIcon
                kind={item.icon}
                x={region.x + (i === 0 ? 103 : 45)}
                y={791}
                size={33}
                fill={green}
              />
              <Lines
                x={region.x + (i === 0 ? 163 : 102)}
                y={816}
                lines={[item.text]}
                size={23}
                weight={600}
                fill={heading}
              />
            </g>
          );
        })}
      </g>
    </>
  );
}

function MobileStage({
  index,
  children
}: {
  index: number;
  children: ReactNode;
}) {
  return (
    <li className={styles.mobileStage}>
      <span
        className={styles.mobileBadge}
        aria-hidden="true"
      >
        {index + 1}
      </span>
      <h3>{accountDiagram.stages[index].title}</h3>
      {children}
      {index < 3 && (
        <span
          className={styles.mobileArrow}
          aria-hidden="true"
        >
          ↓
        </span>
      )}
    </li>
  );
}

function MobileAccountDiagram() {
  return (
    <div className={styles.mobile}>
      <ol className={styles.mobileStages}>
        <MobileStage index={0}>
          <div className={styles.mobileTiers}>
            {accountTiers.map((tier, i) => (
              <div
                key={tier.name}
                className={styles.mobileTier}
              >
                <DiagramIcon
                  kind={i === 0 ? 'people' : 'building'}
                  size={36}
                />
                <div>
                  <h4>{tier.name}</h4>
                  <p>{tier.lines.join(' ')}</p>
                </div>
              </div>
            ))}
          </div>
        </MobileStage>
        <MobileStage index={1}>
          <div className={styles.mobileSignals}>
            {accountSignalCards.map((card) => (
              <div key={card.icon}>
                <span className={styles.iconDisc}>
                  <DiagramIcon
                    kind={card.icon}
                    size={29}
                  />
                </span>
                <h4>{card.title.join(' ')}</h4>
              </div>
            ))}
          </div>
        </MobileStage>
        <MobileStage index={2}>
          <div className={styles.mobilePriorities}>
            {weeklyAccountPriorities.map((item) => (
              <div key={item.count}>
                <strong>{item.count}</strong>
                <p>{item.lines.join(' ')}</p>
              </div>
            ))}
          </div>
        </MobileStage>
        <MobileStage index={3}>
          <div className={styles.mobileBrief}>
            <header>
              <span
                className={styles.companyMark}
                aria-hidden="true"
              >
                B
              </span>
              <div>
                <h4>{brief.company}</h4>
                <div className={styles.chips}>
                  <span>{brief.tier}</span>
                  <span>{brief.freshness}</span>
                </div>
              </div>
            </header>
            <dl>
              <div>
                <dt>
                  <DiagramIcon
                    kind="bulb"
                    size={19}
                  />
                  Why now
                </dt>
                <dd>{brief.whyNow}</dd>
              </div>
              <div>
                <dt>
                  <DiagramIcon
                    kind="news"
                    size={19}
                  />
                  Evidence
                </dt>
                <dd>
                  <ul>
                    {brief.evidence.map((item) => (
                      <li key={item.date}>
                        <span>{item.date}</span>
                        <p>{item.text}</p>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
              <div>
                <dt>
                  <DiagramIcon
                    kind="target"
                    size={19}
                  />
                  Next move
                </dt>
                <dd>{brief.nextMove}</dd>
              </div>
              <div>
                <dt>
                  <DiagramIcon
                    kind="person"
                    size={19}
                  />
                  Owner
                </dt>
                <dd>{brief.owner}</dd>
              </div>
            </dl>
          </div>
        </MobileStage>
      </ol>
      <div className={styles.mobileOutcomes}>
        <p>Outcomes</p>
        <ul>
          {accountPriorityOutcomes.map((item) => (
            <li key={item.text}>
              <DiagramIcon
                kind={item.icon}
                size={27}
              />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Native vectors and text, preserving the original artwork's stage geometry. */
export function AccountPrioritizationDiagram({
  focus = -1,
  progressive = false
}: {
  focus?: number;
  progressive?: boolean;
}) {
  const prefix = `account-native-${useId().replace(/:/g, '')}`;
  return (
    <div
      className={styles.diagram}
      data-native-account-diagram
      data-progressive={progressive}
    >
      <svg
        className={styles.desktop}
        viewBox="0 65 1774 790"
        role="img"
        aria-labelledby={`${prefix}-title ${prefix}-description`}
      >
        <title id={`${prefix}-title`}>Continuous account prioritization</title>
        <desc id={`${prefix}-description`}>
          {accountDiagram.stages
            .map((stage) => `${stage.title}. ${stage.description}`)
            .join(' ')}{' '}
          {accountDiagram.outcome}
        </desc>
        <NativeAccountScene
          focus={focus}
          prefix={prefix}
        />
      </svg>
      <MobileAccountDiagram />
    </div>
  );
}
